import React, { useState } from 'react';
import PopupBase from '../base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import Toast from '../../components/support/Toast';
import IconSave from '@mui/icons-material/SaveAltOutlined';
import IconDelete from '@mui/icons-material/Delete';
import { FormControl, InputAdornment, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { DataProviderInstance } from '../../api/DataProvider';
import { BODY_FAT_SOURCES, BODY_MEASURES, BODY_MEASURE_GROUP, groupBodyMeasures } from '../../pages/appointment/modules/bodyMeasures';
import { getSkinfoldProtocol, protocolFolds } from '../../pages/appointment/components/bodyMeasurements/bodyComposition';
import SkinfoldSection from './SkinfoldSection';
import {
  REFERENCE_PREVIOUS,
  computeDelta,
  findReference,
  formatDate,
  formatDelta,
  formatNumber,
  getValue,
  parseInput,
  sortByDateDesc,
  validateInput,
} from '../../pages/appointment/components/bodyMeasurements/bodyMeasurementsMath';
import styles from './CreateBodyMeasurementsPopup.module.css';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * Registro ou edição de uma avaliação de medidas corporais.
 *
 * Todas as medidas do catálogo, em linhas compactas agrupadas por região:
 * rótulo, campo curto com a unidade e, ao lado, o valor da avaliação anterior
 * com a variação ao digitar. Nenhum campo é obrigatório: basta uma medida
 * para registrar, mas cada uma tem faixa plausível (altura de 11 cm não passa).
 * As dobras seguem o protocolo escolhido (`SkinfoldSection`); só as dobras
 * do protocolo são gravadas. `variant` e `birthday` alimentam o cálculo ao vivo.
 */
function CreateBodyMeasurementsPopup({ patientId, appointmentId, entry, history, variant, birthday, OnHide }) {
  const { SetPatientBodyMeasurements, UpdatePatientBodyMeasurements, DeletePatientBodyMeasurements } = DataProviderInstance();

  const keys = BODY_MEASURES.map((measure) => measure.key);
  const skinfoldKeys = BODY_MEASURES.filter((measure) => measure.group === BODY_MEASURE_GROUP.SKINFOLDS).map((measure) => measure.key);
  const groups = groupBodyMeasures();
  const previous = entry
    ? findReference(history, entry, REFERENCE_PREVIOUS)
    : sortByDateDesc(history)[0] || null;

  const withCurrentTime = (date) => dayjs(date)
    .set('hour', dayjs().hour())
    .set('minute', dayjs().minute())
    .set('second', dayjs().second())
    .format(DATE_FORMAT);

  const [date, setDate] = useState(entry ? dayjs(entry.measuredAt).format(DATE_FORMAT) : withCurrentTime(dayjs()));
  const [values, setValues] = useState(() => {
    const initial = {};
    keys.forEach((key) => {
      const value = getValue(entry, key);
      initial[key] = value === null ? '' : String(value).replace('.', ',');
    });
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [bodyFatSource, setBodyFatSource] = useState(entry?.bodyFatSource || '');
  const [skinfoldProtocol, setSkinfoldProtocol] = useState(getSkinfoldProtocol(entry?.skinfoldProtocol) ? entry.skinfoldProtocol : '');
  const [notes, setNotes] = useState(entry?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'error' });

  const showToast = (message, severity = 'error') => setToast({ open: true, message, severity });

  const bodyFatFilled = parseInput(values.body_fat) !== null;
  const bodyFatSourceMissing = bodyFatFilled && !bodyFatSource;

  function handleValueChange(key, raw) {
    setValues((current) => ({ ...current, [key]: raw }));
    setErrors((current) => ({ ...current, [key]: validateInput(key, raw) }));
  }

  function buildPayload() {
    const measurements = {};
    let invalid = false;
    // Dobras fora do protocolo escolhido não são gravadas
    const keptFolds = protocolFolds(skinfoldProtocol, variant);

    keys.filter((key) => !skinfoldKeys.includes(key) || keptFolds.includes(key)).forEach((key) => {
      const error = validateInput(key, values[key]);
      if (error) invalid = true;
      const parsed = parseInput(values[key]);
      if (parsed !== null) measurements[key] = parsed;
    });

    if (invalid) {
      showToast('Corrija os valores destacados.');
      return null;
    }
    if (Object.keys(measurements).length === 0) {
      showToast('Informe pelo menos uma medida.');
      return null;
    }
    if (!date) {
      showToast('Informe a data da avaliação.');
      return null;
    }
    if (bodyFatSourceMissing) {
      showToast('Informe como a gordura corporal foi medida.');
      return null;
    }

    return {
      measuredAt: date,
      measurements,
      bodyFatSource: measurements.body_fat !== undefined ? bodyFatSource : '',
      skinfoldProtocol: keptFolds.some((key) => measurements[key] !== undefined) ? skinfoldProtocol : '',
      notes: notes.trim(),
    };
  }

  function saveHandler() {
    if (saving) return;
    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    const done = (response) => {
      setSaving(false);
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast(response?.error || 'Não foi possível salvar a avaliação.');
      }
    };

    if (entry) {
      UpdatePatientBodyMeasurements(entry.id, payload, done);
    } else {
      SetPatientBodyMeasurements(patientId, appointmentId, payload, done);
    }
  }

  function deleteHandler() {
    DeletePatientBodyMeasurements(entry.id, (response) => {
      if (response && response.status === 'SUCCESS') {
        OnHide(true);
      } else {
        showToast('Não foi possível excluir a avaliação.');
      }
    });
  }

  function handleDateChange(newValue) {
    setDate(newValue ? withCurrentTime(newValue) : null);
  }

  function renderHint(measure) {
    if (errors[measure.key]) {
      return <span className={`${styles.rowHint} ${styles.rowError}`}>{errors[measure.key]}</span>;
    }
    const previousValue = previous ? getValue(previous, measure.key) : null;
    if (previousValue === null) return <span className={styles.rowHint} />;

    const typed = parseInput(values[measure.key]);
    const delta = typed === null ? null : formatDelta(computeDelta(typed, previousValue), measure.key);
    return (
      <span className={styles.rowHint}>
        anterior {formatNumber(previousValue, measure.decimals)}
        {delta && <span className={styles.deltaPill}>{delta}</span>}
      </span>
    );
  }

  function renderRow(measure) {
    return (
      <div key={measure.key} className={styles.row}>
        <span className={styles.rowLabel}>{measure.label}</span>
        <TextField
          size="small"
          value={values[measure.key] || ''}
          onChange={(e) => handleValueChange(measure.key, e.target.value)}
          error={!!errors[measure.key]}
          inputProps={{ inputMode: 'decimal', maxLength: 7, className: styles.input }}
          InputProps={{ endAdornment: <InputAdornment position="end">{measure.unit}</InputAdornment> }}
          className={styles.field}
        />
        {renderHint(measure)}
      </div>
    );
  }

  const content = (
    <div className={styles.container}>
      <div className={styles.header}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Data da avaliação"
            value={date ? dayjs(date) : null}
            format="DD/MM/YYYY"
            disableFuture={true}
            onChange={handleDateChange}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        {previous && (
          <span className={styles.comparing}>
            Valores anteriores da avaliação de {formatDate(previous.measuredAt)}
          </span>
        )}
      </div>

      {groups.map((group) => (group.key === BODY_MEASURE_GROUP.SKINFOLDS ? (
        <SkinfoldSection
          key={group.key}
          protocol={skinfoldProtocol}
          onProtocolChange={setSkinfoldProtocol}
          values={values}
          variant={variant}
          birthday={birthday}
          date={date}
          renderRow={renderRow}
        />
      ) : (
        <div key={group.key} className={styles.section}>
          <span className={styles.sectionTitle}>{group.label}</span>
          <div className={styles.rows}>
            {group.measures.map(renderRow)}

            {group.measures.some((measure) => measure.key === 'body_fat') && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>Como foi medida</span>
                <FormControl size="small" error={bodyFatSourceMissing} className={styles.sourceField}>
                  <Select
                    displayEmpty
                    value={bodyFatSource}
                    onChange={(e) => setBodyFatSource(e.target.value)}
                    renderValue={(value) => {
                      const source = BODY_FAT_SOURCES.find((item) => item.key === value);
                      return source ? source.label : <span className={styles.placeholder}>Método</span>;
                    }}
                  >
                    {BODY_FAT_SOURCES.map((source) => (
                      <MenuItem key={source.key} value={source.key}>{source.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}
          </div>
        </div>
      )))}

      <TextField
        label="Observações"
        size="small"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        multiline
        minRows={2}
        fullWidth
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      {entry && (
        <CustomButton
          variant="contained"
          style="error"
          label="Excluir"
          icon={<IconDelete />}
          onClick={() => setShowConfirmDelete(true)}
          enabled={!saving}
        />
      )}

      <CustomButton
        variant="outlined"
        style="secondary"
        label="Cancelar"
        onClick={() => OnHide(false)}
        enabled={!saving}
      />

      <CustomButton
        style="primary"
        variant="contained"
        label={entry ? 'Atualizar' : 'Registrar'}
        icon={<IconSave />}
        onClick={saveHandler}
        loading={saving}
        enabled={!saving}
      />

      {showConfirmDelete && (
        <ConfirmPopup
          Visibility={showConfirmDelete}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir esta avaliação?"
          ConfirmLabel="Excluir"
          OnConfirm={deleteHandler}
          OnHide={() => setShowConfirmDelete(false)}
        />
      )}
    </div>
  );

  return (
    <PopupBase
      Title={entry ? 'Editar avaliação' : 'Nova avaliação'}
      Content={content}
      Footer={footer}
      OnClose={() => OnHide(false)}
      maxWidth="md"
    />
  );
}

export default CreateBodyMeasurementsPopup;
